import { HomeTwoTone } from "@ant-design/icons";
import { App } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import useIndexedDB from "blockly-turtle-common/hooks/useIndexedDB";
import { IndexedDBConfig } from "@/IndexedDBConfig";
import { CodeCreate, CodeRead, CodeType } from "@openapi";
import CodeTableBase from "./CodeTableBase";
import useLocale from "@/hooks/useLocale";
import {
    type CodeListFilter,
    useCodeListWindow,
} from "@/hooks/useCodeListWindow";
import { generateUUID } from "@/lib/uuid";

interface CodeTableProps {}

function getEditorUrl(type: CodeType, id: string): string {
    switch (type) {
        case CodeType.Logo:
            return `/logo-code?id=${id}&source=local`;
        case CodeType.LogoBlockly:
            return `/logo-blocks?id=${id}&source=local`;
        case CodeType.Python:
            return `/python-code?id=${id}&source=local`;
        case CodeType.PythonBlockly:
            return `/python-blocks?id=${id}&source=local`;
        case CodeType.PythonTurtle:
            return `/python-turtle-code?id=${id}&source=local`;
        case CodeType.PythonTurtleBlockly:
            return `/python-turtle-blocks?id=${id}&source=local`;
        case CodeType.Cpp:
            return `/cpp-code?id=${id}&source=local`;
        case CodeType.PybricksBlockly:
            return `/pybricks-blocks?id=${id}&source=local`;
        case CodeType.Pybricks:
            return `/pybricks-code?id=${id}&source=local`;
        case CodeType.Soldered:
            return `/soldered-code?id=${id}&source=local`;
        default:
            return type satisfies never;
    }
}

const CodeTableLocal: React.FC<CodeTableProps> = () => {
    const { notification } = App.useApp();
    const [codes, setCodes] = useState<CodeRead[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const locale = useLocale();
    // The whole list is in the browser; it is filtered and paged here.
    const [filter, setFilter] = useState<CodeListFilter>({
        term: "",
        types: [],
        order: "descend",
    });
    const { visible, total, hasMore, showMore } = useCodeListWindow(
        codes,
        filter,
    );

    const {
        put: putFile,
        getAll: getAllFiles,
        delete: deleteFile,
    } = useIndexedDB(
        IndexedDBConfig.name,
        IndexedDBConfig.version,
        IndexedDBConfig.storeName,
        IndexedDBConfig.storeConfig,
    );

    const fetchCodesLocal = useCallback(async () => {
        try {
            const response = await getAllFiles<CodeRead>();
            setCodes(response || []);
        } catch (error) {
            console.error("Error fetching codes:", error);
        } finally {
            setLoading(false);
        }
    }, [getAllFiles]);

    useEffect(() => {
        setTimeout(fetchCodesLocal);
    }, [fetchCodesLocal]);

    const handleSave = useCallback(
        async (newCode: CodeCreate) => {
            try {
                // create CodeRead type variable with generate uuid
                const codeId = generateUUID();
                const code: CodeRead = {
                    ...newCode,
                    id: codeId,
                    created_at: new Date(),
                    last_used: new Date(),
                };

                await putFile<CodeCreate>(code);
                // Redirect to the editor page for the newly created code
                const editorUrl = getEditorUrl(code.type, code.id);
                window.location.href = `/${locale}${editorUrl}`;
            } catch (error) {
                console.error("Error saving code localy:", error);
            }
        },
        [putFile, locale],
    );

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                await deleteFile(id);
                setCodes((prevCodes) =>
                    prevCodes.filter((code) => code.id !== id),
                );
                notification.success({
                    title: "Code Deleted",
                    description: `The code "${id}" has been successfully deleted.`,
                    placement: "bottomRight",
                });
            } catch (error) {
                console.error("Error deleting code:", error);
                notification.error({
                    title: "Deletion Failed",
                    description: `An error occurred while deleting the code "${id}".`,
                    placement: "bottomRight",
                });
            }
        },
        [deleteFile, notification],
    );

    return (
        <CodeTableBase
            icon={<HomeTwoTone />}
            rows={visible}
            total={total}
            hasMore={hasMore}
            onLoadMore={showMore}
            filter={filter}
            onFilterChange={setFilter}
            loading={loading}
            handleSave={handleSave}
            handleDelete={handleDelete}
            getEditorUrl={getEditorUrl}
            isLocal={true}
        />
    );
};

export default CodeTableLocal;
